// Киоск-режим дашборда стенда: полноэкранная панель без логина, для
// цеховых компов/телевизоров и «поделиться посмотреть».
//
// Два способа входа:
//  1. /kiosk/rig?token=mespat_… — прямой PAT (цеховые компы, настраивает
//     админ).  Токен запоминается в localStorage и убирается из адресной
//     строки, чтобы не светился на экране.
//  2. /kiosk/rig без токена — форма с общим паролем просмотра: страница
//     меняет пароль на тот же read-only PAT через POST /kiosk-login
//     (env KIOSK_VIEW_PASSWORD_HASH / KIOSK_VIEW_TOKEN на сервере).
//
// В обоих случаях токен — PAT оператора с ролью KIOSK: телеметрию читает,
// настройки менять не может (PUT /config требует операторский JWT).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RigDashboardView } from "@/pages/admin/MembraneDashboard";
import { ApiError, membraneRigApi, setStaticApiToken } from "@/lib/api";
import { useSystemSettings } from "@/lib/system";

const TOKEN_KEY = "mes:kiosk:token";

function PasswordGate({ onToken }: { onToken: (token: string) => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<"wrong" | "disabled" | "other" | null>(null);

  const submit = async () => {
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await membraneRigApi.kioskLogin(password);
      onToken(token);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setError("wrong");
      else if (e instanceof ApiError && e.status === 404) setError("disabled");
      else setError("other");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <form
        className="card p-6 w-full max-w-sm space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="text-lg font-semibold text-text-primary">
          {t("rigKiosk.passwordTitle")}
        </div>
        <p className="text-sm text-text-muted">{t("rigKiosk.passwordHint")}</p>
        <input
          type="password"
          autoFocus
          className="w-full px-3 py-2 rounded-md border border-border bg-page text-text-primary"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("rigKiosk.passwordPlaceholder")}
        />
        {error === "wrong" && (
          <p className="text-sm text-danger">{t("rigKiosk.wrongPassword")}</p>
        )}
        {error === "disabled" && (
          <p className="text-sm text-danger">{t("rigKiosk.passwordDisabled")}</p>
        )}
        {error === "other" && (
          <p className="text-sm text-danger">{t("rigKiosk.loginFailed")}</p>
        )}
        <button type="submit" className="btn btn-primary w-full" disabled={!password || busy}>
          {busy ? "…" : t("rigKiosk.enter")}
        </button>
      </form>
    </div>
  );
}

export default function RigKiosk() {
  const { t } = useTranslation();
  const system = useSystemSettings();
  const [state, setState] = useState<"loading" | "gate" | "ready">("loading");

  useEffect(() => {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get("token");
    if (fromUrl) {
      localStorage.setItem(TOKEN_KEY, fromUrl);
      url.searchParams.delete("token");
      window.history.replaceState(null, "", url.toString());
    }
    const token = fromUrl ?? localStorage.getItem(TOKEN_KEY);
    if (token) {
      setStaticApiToken(token);
      setState("ready");
    } else {
      setState("gate");
    }
    // Отозванный/протухший токен: сбрасываем и возвращаем форму пароля,
    // иначе киоск навсегда замирал бы с мёртвыми запросами.
    const onAuthFailed = () => {
      localStorage.removeItem(TOKEN_KEY);
      setStaticApiToken(null);
      setState("gate");
    };
    window.addEventListener("mes:kiosk-auth-failed", onAuthFailed);
    // Уходя со страницы, возвращаем обычную авторизацию — иначе оператор,
    // открывший после киоска админку в той же вкладке, ходил бы под PAT.
    return () => {
      window.removeEventListener("mes:kiosk-auth-failed", onAuthFailed);
      setStaticApiToken(null);
    };
  }, []);

  if (state === "loading" || system.isLoading) return null;
  if (system.data && !system.data.modules.includes("membrane-bench")) {
    return <p className="p-10 text-center text-text-muted">{t("common.moduleDisabled")}</p>;
  }

  if (state === "gate") {
    return (
      <PasswordGate
        onToken={(token) => {
          localStorage.setItem(TOKEN_KEY, token);
          setStaticApiToken(token);
          setState("ready");
        }}
      />
    );
  }

  return (
    <div className="min-h-full">
      <div className="container-page py-5">
        <RigDashboardView kiosk />
      </div>
    </div>
  );
}
